import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import type { ContractSupplementApplyHistoryForPath } from '../entities/AppliedContract.js';
import { type ContractSupplementChange, ContractSupplementDataType } from '../entities/ContractSupplement.js';
import { getParsedValueOfSupplementChange } from './getParsedValueOfSupplementChange.js';

export type ChangeOperation = 'added' | 'removed' | 'changed';

interface ChangeOperationPresentation {
  label: string;
  /** literal iconify token, interpolated classes are not picked up by the tailwind scanner */
  iconClass: string;
  containerClass: string;
  textClass: string;
}

/**
 * The single visual language for supplement changes. `warning` is deliberately not used here, it is reserved
 * module wide for "needs attention" states like an unsigned supplement.
 */
export const CHANGE_OPERATION_PRESENTATION: Record<ChangeOperation, ChangeOperationPresentation> = {
  added: {
    label: 'Hinzugefügt',
    iconClass: 'icon-[material-symbols--add-circle-outline]',
    containerClass: 'bg-success/10 border-success/30',
    textClass: 'text-success',
  },
  removed: {
    label: 'Entfernt',
    iconClass: 'icon-[material-symbols--delete-outline]',
    containerClass: 'bg-error/10 border-error/30',
    textClass: 'text-error',
  },
  changed: {
    label: 'Geändert',
    iconClass: 'icon-[material-symbols--edit-outline]',
    containerClass: 'bg-info/10 border-info/30',
    textClass: 'text-info',
  },
};

type ChangeLike = NarrowedEntity<ContractSupplementChange, { path: 1; value: 1; type: 1 }>;

const isRemoval = (change: ChangeLike) =>
  change.type === ContractSupplementDataType.NULL ||
  isNullish(change.value) ||
  change.value === '' ||
  (change.type !== ContractSupplementDataType.NEW_FIELD && change.value === '[]');

/**
 * Classifies a change so it can be rendered consistently. A change counts as an addition when the field is new
 * or when there is no previous value in the history, everything else that is not a removal is a modification.
 */
export const getChangeOperation = (
  change: Maybe<ChangeLike>,
  history?: Maybe<ContractSupplementApplyHistoryForPath>,
): ChangeOperation => {
  if (!change) return 'changed';
  if (isRemoval(change)) return 'removed';
  if (change.type === ContractSupplementDataType.NEW_FIELD) return 'added';
  return history?.changes?.length ? 'changed' : 'added';
};

const summarizeObject = (value: object): string => {
  const entries = Object.entries(value).filter(([key]) => key !== '_class' && key !== '_id');
  const shown = entries.slice(0, 3).map(([key, entryValue]) => `${key}: ${String(entryValue)}`);
  return entries.length > shown.length ? `${shown.join(', ')} …` : shown.join(', ');
};

/**
 * Human readable text for a single change value. Handles the JSON type as well, which is why callers should
 * prefer this over hand rolled `String(value)` conversions that would end up as `[object Object]`.
 */
export const getReadableChangeText = (change: Maybe<ChangeLike>): string => {
  if (!change) return 'X';

  const parsed = getParsedValueOfSupplementChange(change);

  if (change.type === ContractSupplementDataType.NULL) return 'X';
  if (change.type === ContractSupplementDataType.BOOLEAN) return parsed ? 'Ja' : 'Nein';
  if (change.type === ContractSupplementDataType.DATE && parsed instanceof Date)
    return formatCalendarDay(parsed, 'dd.MM.yyyy');
  if (change.type === ContractSupplementDataType.STRING && isNullish(parsed)) return 'X';
  if (change.type === ContractSupplementDataType.NEW_FIELD && isNullish(parsed)) return 'Neu erstellt';

  if (Array.isArray(parsed)) {
    if (!parsed.length) return 'Keine Einträge';
    return parsed.length === 1 ? '1 Eintrag' : `${parsed.length} Einträge`;
  }

  if (typeof parsed === 'object' && !isNullish(parsed)) return summarizeObject(parsed);

  return String(parsed ?? 'X');
};
